// pages/mobile/DispatchPage.js
// Dispatch details with confirm / can't make it (mockups 2b, 2c, 2d). Shows
// every field of the WhatsApp dispatch message plus the extra detail around it.
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AnswerBar from './AnswerBar';
import { DetailTopBar, LoadState, StatusPill } from './MobileChrome';
import { NavigateIcon, PeopleIcon, PlowIcon, TruckIcon } from './icons';
import { DISPATCH_TYPE_LABELS, formatDay, formatSent, formatTime, mapsLink } from './format';
import { fetchDetail, useMobileData } from './mobileApi';
import styles from './mobile.module.css';
import useMessageRead from './useMessageRead';

const ANSWER_LABELS = {
  confirm: 'Confirm',
  deny: "Can't make it",
  denyTitle: "Can't make this dispatch?",
  denySubmit: "Tell dispatch I can't make it",
  confirmedToast: 'Confirmed. Sent to dispatch.',
  deniedToast: "Sent: you can't make it.",
  confirmedState: "You're confirmed for this dispatch.",
  deniedState: "You told dispatch you can't make it.",
};

const EquipmentRow = ({ icon, label, value, gold }) => (
  <div className={styles.cardRow}>
    <span className={gold ? `${styles.tile} ${styles.tileGold}` : styles.tile}>{icon}</span>
    <span>
      <span className={styles.rowLabel}>{label}</span>
      <br />
      <span className={styles.rowValue}>{value}</span>
    </span>
  </div>
);

const DispatchPage = () => {
  const { id } = useParams();
  const load = useCallback(() => fetchDetail('dispatch', id), [id]);
  const { data: dispatch, error, loading, reload } = useMobileData(load);
  useMessageRead(dispatch);

  if (!dispatch) {
    return (
      <main className={styles.page}>
        <DetailTopBar title="Dispatch" />
        <LoadState loading={loading} error={error} onRetry={reload} />
      </main>
    );
  }

  const storm = dispatch.storm;
  return (
    <main className={`${styles.page} ${styles.pageWithActions}`}>
      <DetailTopBar title={`Dispatch #${dispatch.id}`} />
      <div className={styles.content}>
        <section className={styles.hero} aria-label="Dispatch summary">
          <div className={styles.heroTop}>
            <span className={styles.typeChip}>{DISPATCH_TYPE_LABELS[dispatch.dispatch_type] || 'Dispatch'}</span>
            <StatusPill status={dispatch.status} needsAnswer={dispatch.needs_answer} />
          </div>
          <div>
            <div className={styles.heroLabel}>{dispatch.meeting_test ? 'MEETING TEST — no work assignment' : `${dispatch.staff_name}, you're dispatched for`}</div>
            <div className={styles.heroTime}>{formatTime(dispatch.dispatched_for)}</div>
            <div className={styles.heroDay}>{formatDay(dispatch.dispatched_for)} · Eastern</div>
          </div>
          {storm && (
            <div className={styles.heroStats}>
              <div className={styles.heroStat}>
                <span className={styles.heroStatLabel}>Storm</span>
                <span className={styles.heroStatValue}>{storm.type || '—'}</span>
              </div>
              <div className={styles.heroStat}>
                <span className={styles.heroStatLabel}>Severity</span>
                <span className={styles.heroStatValue}>{storm.severity || '—'}</span>
              </div>
            </div>
          )}
          {storm && (storm.snow_amount || storm.starts_at) && (
            <span className={styles.heroNote}>
              Forecast: {[storm.snow_amount && `${storm.snow_amount} cm`, storm.starts_at && `starting ${formatTime(storm.starts_at)} ${formatDay(storm.starts_at)}`]
                .filter(Boolean)
                .join(', ')}
            </span>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Your equipment</h2>
          <div className={styles.cardList}>
            <EquipmentRow icon={<TruckIcon />} label="Vehicle" value={dispatch.vehicle || 'Not assigned'} />
            {dispatch.plow && <EquipmentRow icon={<PlowIcon />} label="Plow" value={dispatch.plow} />}
            {dispatch.double_down && <EquipmentRow icon={<PeopleIcon />} label="Double down" value={dispatch.double_down} gold />}
            {dispatch.equipment.length > 0 && (
              <EquipmentRow icon={<PlowIcon />} label="Equipment" value={dispatch.equipment.join(', ')} />
            )}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Sites · in this order</h2>
          <ol className={styles.siteList}>
            {dispatch.sites.map((site) => {
              const link = dispatch.meeting_test ? null : mapsLink(site.latitude, site.longitude, site.address);
              return (
                <li key={site.order} className={styles.site}>
                  <span className={styles.siteNumber}>{site.order}</span>
                  <span className={styles.siteBody}>
                    <span className={styles.siteName}>{site.name}</span>
                    {site.address && <span className={styles.mutedText}>{site.address}</span>}
                    {site.notes && <span className={styles.siteNote}>{site.notes}</span>}
                  </span>
                  {link && (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Navigate to ${site.name}`}
                      className={`${styles.iconButton} ${styles.iconButtonTinted}`}
                    >
                      <NavigateIcon />
                    </a>
                  )}
                </li>
              );
            })}
          </ol>
        </section>

        {dispatch.message && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Message from dispatch</h2>
            <div className={styles.card}>
              <p className={styles.bodyText}>{dispatch.message}</p>
            </div>
          </section>
        )}

        <section className={styles.cardList} aria-label="Details">
          <div className={styles.detailRow}><span>Sent</span><span>{formatSent(dispatch.dispatched_at)}</span></div>
        </section>
      </div>

      <AnswerBar
          kind="dispatch"
        id={dispatch.id}
        status={dispatch.status}
        needsAnswer={dispatch.needs_answer}
        canAnswer={dispatch.can_answer}
        closedReason={dispatch.closed_reason}
        labels={ANSWER_LABELS}
        onChanged={reload}
      />
    </main>
  );
};

export default DispatchPage;
